// ==========================
// Version 15 — MemoryForm.js
// - Add optional "time" field for when the memory happened
// - Store `time` in Firestore and in local newMemory object
// ==========================
import React, { useEffect, useRef, useState } from 'react';
import MapPicker from './MapPicker';
import { useAuth } from './context/AuthContext';
import { addMemoryDoc, uploadFile } from './db';
import { reserveWriteSlot } from './limits';
import { toast } from './utils/toast';
import useUserPrefs from './hooks/useUserPrefs';

const NAVY = '#0A1A33';
const GOLD = '#C9A44C';
const IVORY = '#FDFCF9';
const WARM_GRAY = '#888888';

const TITLE_MAX = 80;

function MemoryForm({ onAddMemory }) {
  const { currentUser } = useAuth();
  const uid = currentUser?.uid || null;

  // 🔄 Live user prefs from Firestore (users/{uid})
  const { defaults } = useUserPrefs(uid);

  const [location, setLocation] = useState(null);
  const [date, setDate] = useState('');
  const [time, setTime] = useState(''); // NEW: time of memory (HH:MM or HH:MM:SS)
  const [title, setTitle] = useState('');
  const [story, setStory] = useState('');

  // Initialize toggles; then sync them when prefs change (first load or user switch)
  const [isPublic, setIsPublic] = useState(false);
  const [isMediaPublic, setIsMediaPublic] = useState(false);

  // Apply defaults whenever uid changes or Firestore defaults change
  useEffect(() => {
    if (!uid) return;
    setIsPublic(!!defaults.isPublic);
    setIsMediaPublic(!!defaults.isMediaPublic);
  }, [uid, defaults.isPublic, defaults.isMediaPublic]);

  const [localFiles, setLocalFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [imageUrls, setImageUrls] = useState([]);
  const [progress, setProgress] = useState({});
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  // --- Reverse-geocoded fields saved with the memory ---
  const [geo, setGeo] = useState({
    city: null,
    region: null,
    locality: null,
    administrative_area: null,
    country: null,
    countryCode: null,
    placeName: null,
    formattedAddress: null,
  });
  const geocodeTimerRef = useRef(null);

  const parseAddressComponents = (result) => {
    const comps = result?.address_components || [];
    const lookup = (type) => comps.find(c => c.types.includes(type));
    const locality = lookup('locality')?.long_name
      || lookup('postal_town')?.long_name
      || lookup('sublocality')?.long_name
      || null;
    const admin1 = lookup('administrative_area_level_1')?.long_name || null;
    const admin2 = lookup('administrative_area_level_2')?.long_name || null;
    const country = lookup('country')?.long_name || null;
    const countryCode = lookup('country')?.short_name || null;

    const region = admin1 || admin2 || null;
    const city = locality || null;
    const placeName =
      lookup('neighborhood')?.long_name ||
      lookup('political')?.long_name ||
      lookup('point_of_interest')?.long_name ||
      city ||
      null;

    return {
      city,
      region,
      locality,
      administrative_area: admin1 || admin2 || null,
      country,
      countryCode,
      placeName,
      formattedAddress: result?.formatted_address || null,
    };
  };

  useEffect(() => {
    if (!location) {
      setGeo({
        city: null, region: null, locality: null, administrative_area: null,
        country: null, countryCode: null, placeName: null, formattedAddress: null,
      });
      return;
    }

    if (geocodeTimerRef.current) clearTimeout(geocodeTimerRef.current);
    geocodeTimerRef.current = setTimeout(() => {
      const tryGeocode = async () => {
        try {
          const w = window;
          if (!w.google || !w.google.maps || !w.google.maps.Geocoder) return;
          const geocoder = new w.google.maps.Geocoder();
          geocoder.geocode(
            { location: { lat: Number(location.lat), lng: Number(location.lng) } },
            (results, status) => {
              if (status === 'OK' && Array.isArray(results) && results.length) {
                const parsed = parseAddressComponents(results[0]);
                setGeo(parsed);
              }
            }
          );
        } catch {}
      };
      tryGeocode();
    }, 300);

    return () => {
      if (geocodeTimerRef.current) clearTimeout(geocodeTimerRef.current);
    };
  }, [location]);

  const uploadWithTimeout = (fn, ms) =>
    new Promise((resolve, reject) => {
      const t = setTimeout(() => reject(new Error(`Upload timeout (${ms / 1000}s)`)), ms);
      fn().then((v) => { clearTimeout(t); resolve(v); })
        .catch((e) => { clearTimeout(t); reject(e); });
    });

  const addPreviews = (files) => {
    const next = files.map((f) => URL.createObjectURL(f));
    setPreviews((prev) => [...prev, ...next]);
  };

  const revokeAllPreviews = () => {
    previews.forEach((url) => URL.revokeObjectURL(url));
    setPreviews([]);
  };

  const handleChooseFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    if (!currentUser) {
      toast.error('Please log in before uploading photos.');
      e.target.value = '';
      return;
    }

    setLocalFiles((prev) => [...prev, ...files]);
    addPreviews(files);

    try {
      setUploading(true);
      const urls = [];
      for (let i = 0; i < files.length; i++) {
        const absoluteIndex = imageUrls.length + i;
        const onPct = (pct) => setProgress((prev) => ({ ...prev, [absoluteIndex]: pct }));
        const url = await uploadWithTimeout(
          () => uploadFile(files[i], currentUser.uid, {
            mediaPublic: isMediaPublic,
            onProgress: onPct,
          }),
          45000
        );
        urls.push(url);
      }
      setImageUrls((prev) => [...prev, ...urls]);
    } catch (err) {
      console.error('Upload error:', err);
      toast.error('One or more photos failed to upload.');
      setLocalFiles((prev) => prev.slice(0, prev.length - files.length));
      setPreviews((prev) => prev.slice(0, prev.length - files.length));
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const removeLocal = (idx) => {
    setLocalFiles((prev) => prev.filter((_, i) => i !== idx));
    URL.revokeObjectURL(previews[idx]);
    setPreviews((prev) => prev.filter((_, i) => i !== idx));
    setProgress((prev) => {
      const cp = { ...prev };
      delete cp[idx];
      return cp;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!currentUser) return toast.error('You must be logged in to add a memory.');
    if (!location) return toast.error('Please select a location on the map.');
    if (uploading) return toast.error('Please wait until photo uploads finish.');
    if (!title.trim()) return toast.error('Please enter a title.');

    setSaving(true);
    try {
      await reserveWriteSlot(currentUser.uid);

      const cleanUrls = imageUrls.filter((u) => typeof u === 'string' && u.trim());

      const firestoreId = await addMemoryDoc({
        userId: currentUser.uid,
        lat: location.lat,
        lng: location.lng,
        isPublic,
        isMediaPublic,
        city: geo.city || null,
        region: geo.region || null,
        locality: geo.locality || null,
        administrative_area: geo.administrative_area || null,
        country: geo.country || null,
        countryCode: geo.countryCode || null,
        placeName: geo.placeName || null,
        formattedAddress: geo.formattedAddress || null,
        date: date || null,
        time: time || null, // NEW: store the time string in Firestore
        title: title.trim(),
        story,
        imageUrls: cleanUrls,
      });

      const newMemory = {
        id: Date.now(),
        firestoreId,
        userId: currentUser.uid,
        location,
        isPublic,
        isMediaPublic,
        city: geo.city || null,
        region: geo.region || null,
        locality: geo.locality || null,
        administrative_area: geo.administrative_area || null,
        country: geo.country || null,
        countryCode: geo.countryCode || null,
        placeName: geo.placeName || null,
        formattedAddress: geo.formattedAddress || null,
        date: date || null,
        time: time || null, // NEW: keep time on the local memory object
        title: title.trim(),
        story,
        imageUrls: cleanUrls,
        createdAt: new Date().toISOString(),
      };
      onAddMemory?.(newMemory);

      // reset (re-apply current defaults so toggles reflect user prefs)
      setLocation(null);
      setDate('');
      setTime(''); // reset time as well
      setTitle('');
      setStory('');
      setIsPublic(!!defaults.isPublic);
      setIsMediaPublic(!!defaults.isMediaPublic);
      setLocalFiles([]);
      setImageUrls([]);
      setProgress({});
      setGeo({
        city: null, region: null, locality: null, administrative_area: null,
        country: null, countryCode: null, placeName: null, formattedAddress: null,
      });
      revokeAllPreviews();

      toast.success('Memory saved!');
    } catch (err) {
      console.error('Error saving memory:', err);
      toast.error(
        err?.code === 'permission-denied'
          ? 'You are posting too quickly. Please wait and try again.'
          : 'Failed to save memory.'
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={styles.card}>
      <h3 style={styles.title}>Create a Memory</h3>
      <p style={styles.subtitle}>Drop a pin, add a title, write your story, add photos.</p>

      <label style={styles.label} htmlFor="bwak-map-picker">📍 Select Location on Map</label>
      <div style={styles.mapWrap}>
        {/* MapPicker isn’t a native input; we still give it an id for association */}
        <div id="bwak-map-picker" aria-describedby="map-help location-privacy-notice">
          <MapPicker onLocationSelect={setLocation} />
        </div>
      </div>
      <div id="map-help" style={{ position: 'absolute', left: -9999, top: 0 }}>
        Use the map to choose a location. This field is required.
      </div>

      <div id="location-privacy-notice" style={styles.locationNotice}>
        <strong>Your location matters.</strong> BWK saves the exact pin coordinates with
        this memory. A public memory can show this location on the Global Map. If you
        later mint it, the exact coordinates become part of public NFT metadata and may
        remain permanently available. Choose a location you are comfortable sharing.
      </div>

      {location && (
        <div style={styles.detectedLine}>
          <span style={{ opacity: 0.7 }}>Detected:&nbsp;</span>
          <span>
            {(geo.region || geo.city || geo.locality || geo.placeName)
              ? [geo.region, geo.city || geo.locality || geo.placeName].filter(Boolean).join(', ')
              : `${Number(location.lat).toFixed(4)}, ${Number(location.lng).toFixed(4)}`}
          </span>
        </div>
      )}

      <label style={styles.label} htmlFor="memory-title">🗂️ Title</label>
      <div style={{ position: 'relative' }}>
        <input
          id="memory-title"
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value.slice(0, TITLE_MAX))}
          placeholder="Give this memory a name…"
          required
          maxLength={TITLE_MAX}
          style={styles.input}
          aria-describedby="title-counter"
        />
        <div id="title-counter" style={styles.counter} aria-live="polite">
          {title.length}/{TITLE_MAX}
        </div>
      </div>

      <label style={styles.label} htmlFor="memory-date">📅 Date</label>
      <input
        id="memory-date"
        type="date"
        value={date}
        onChange={(e) => setDate(e.target.value)}
        required
        style={styles.input}
      />

      {/* NEW: Time of memory (optional for now) */}
      <label style={styles.label} htmlFor="memory-time">⏰ Time (optional)</label>
      <input
        id="memory-time"
        type="time"
        value={time}
        onChange={(e) => setTime(e.target.value)}
        style={styles.input}
      />

      <label style={styles.label} htmlFor="memory-story">📝 Story</label>
      <textarea
        id="memory-story"
        value={story}
        onChange={(e) => setStory(e.target.value)}
        required
        style={styles.textarea}
        placeholder="Write about this moment…"
      />

      <label style={styles.label} htmlFor="memory-files">🖼️ Photos (optional)</label>
      <input
        id="memory-files"
        type="file"
        accept="image/*"
        multiple
        onChange={handleChooseFiles}
        style={styles.file}
      />

      {previews.length > 0 && (
        <div style={styles.previewRow}>
          {previews.map((src, i) => (
            <div key={i} style={styles.previewWrap}>
              <img src={src} alt="" style={styles.thumb} />
              <button
                type="button"
                title="Remove"
                onClick={() => removeLocal(i)}
                style={styles.removeThumb}
                disabled={uploading}
              >
                ✖
              </button>
              {progress[i] != null && <div style={styles.progressText}>{progress[i]}%</div>}
            </div>
          ))}
        </div>
      )}

      <fieldset style={styles.switchPanel} aria-describedby="visibility-help">
        <legend style={{ fontWeight: 800, fontSize: 13, marginBottom: 6 }}>Visibility</legend>
        <label style={styles.check}>
          <input
            type="checkbox"
            checked={isPublic}
            onChange={(e) => setIsPublic(e.target.checked)}
            style={{ marginRight: 8 }}
          />
          Make this memory public (pin will appear on Global Map)
        </label>

        <label style={styles.check}>
          <input
            type="checkbox"
            checked={isMediaPublic}
            onChange={(e) => setIsMediaPublic(e.target.checked)}
            style={{ marginRight: 8 }}
          />
          Allow media (photos/videos) to be public
          <span style={styles.hint}>&nbsp;guests still won’t see media; only logged-in users will.</span>
        </label>
        <div id="visibility-help" style={{ position: 'absolute', left: -9999, top: 0 }}>
          Choose whether this memory and its media are public.
        </div>
      </fieldset>

      <button type="submit" style={styles.primaryBtn} disabled={saving || uploading}>
        {uploading ? 'Uploading photos…' : saving ? 'Saving…' : 'Save Memory'}
      </button>
    </form>
  );
}

const focusRing = {
  outline: 'none',
  boxShadow: `0 0 0 3px rgba(201,164,76,0.35)`,
  borderColor: GOLD,
};

const styles = {
  card: {
    display: 'flex', flexDirection: 'column', gap: 12,
    backgroundColor: IVORY, padding: 20, borderRadius: 16,
    boxShadow: '0 8px 28px rgba(10,26,51,0.25)',
    border: `1px solid rgba(201,164,76,0.35)`,
    marginTop: 20, color: NAVY
  },
  title: { margin: 0, fontFamily: 'serif', fontSize: 20 },
  subtitle: { margin: '2px 0 6px', color: WARM_GRAY, fontSize: 14 },
  label: { fontWeight: 700, marginTop: 6 },
  mapWrap: { borderRadius: 12, overflow: 'hidden', border: `1px solid rgba(10,26,51,0.15)` },
  locationNotice: {
    padding: '10px 12px',
    borderRadius: 10,
    border: '1px solid rgba(138,109,46,0.34)',
    background: 'rgba(201,164,76,0.09)',
    color: NAVY,
    fontSize: 13,
    lineHeight: 1.5,
  },
  detectedLine: { marginTop: 8, marginBottom: 4, fontSize: 13, color: WARM_GRAY },
  input: {
    padding: 12, borderRadius: 12,
    border: `1px solid rgba(10,26,51,0.15)`,
    background: '#fff', color: NAVY,
    outline: 'none', fontSize: 14
  },
  textarea: {
    padding: 12, borderRadius: 12,
    border: `1px solid rgba(10,26,51,0.15)`,
    minHeight: 110, background: '#fff',
    color: NAVY, outline: 'none', fontSize: 14,
    lineHeight: 1.5, resize: 'vertical'
  },
  file: {
    padding: 8, borderRadius: 12,
    border: `1px solid rgba(10,26,51,0.15)`,
    background: '#fff'
  },
  check: { display: 'flex', alignItems: 'center', fontSize: 14, color: NAVY },
  hint: { color: WARM_GRAY, fontStyle: 'italic' },
  primaryBtn: {
    padding: '12px 14px', borderRadius: 12, border: 'none',
    backgroundColor: GOLD, color: NAVY,
    fontWeight: 800, cursor: 'pointer',
    fontSize: 15, marginTop: 4
  },
  previewRow: { display: 'flex', gap: 12, flexWrap: 'wrap' },
  previewWrap: { position: 'relative' },
  thumb: {
    width: 72, height: 72, objectFit: 'cover',
    borderRadius: 10, border: `1px solid rgba(201,164,76,0.35)`,
    background: '#fff'
  },
  removeThumb: {
    position: 'absolute', top: -8, right: -8,
    border: 'none', background: GOLD, color: NAVY,
    borderRadius: '50%', width: 22, height: 22,
    cursor: 'pointer', fontSize: 12, lineHeight: '22px',
    boxShadow: '0 4px 10px rgba(10,26,51,0.25)'
  },
  progressText: {
    position: 'absolute', bottom: -18, left: 0, right: 0,
    textAlign: 'center', fontSize: 12, color: WARM_GRAY
  },
  switchPanel: {
    borderTop: `1px dashed rgba(10,26,51,0.12)`,
    paddingTop: 10, marginTop: 4
  },
  counter: {
    position: 'absolute', right: 10, top: '50%',
    transform: 'translateY(-50%)', fontSize: 12, color: WARM_GRAY
  },
};

['input', 'textarea', 'button'].forEach(() => {});

export default MemoryForm;
// ==========================
// End Version 15 — MemoryForm.js
// ==========================
